import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTableStateEvent } from 'primereact/datatable';
import { classNames } from 'primereact/utils';
import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import InfoIconPrimary from '../../assets/icons/InfoIconPrimary';
import EmptyComponentsImg from '../../assets/images/empty-components.svg';
import DownloadJsonFileButton from '../../components/download-pcf/DownloadJsonFileButton';
import DownloadPdfFileButton from '../../components/download-pcf/DownloadPdfFileButton';
import EmptyScreen from '../../components/empty-screen/EmptyScreen';
import RequestStatus from '../../components/request-status/RequestStatus';
import { useGetPaginatedComponents } from '../../hooks/api/components';
import { useCreatePcfRequest } from '../../hooks/api/request-pcf';
import { useAuth } from '../../hooks/auth/useAuth';
import useDialogContext from '../../hooks/dialog/useDialogContext';
import useNotificationContext from '../../hooks/notification/useNotificationContext';
import { useComponentsTableQueryParams } from '../../hooks/useComponentsTableQueryParams';
import ComponentRegion from '../../shared/components/component-region/ComponentRegion';
import { AppRoutes } from '../../shared/enums/app-routes';
import { PCFBiogenicEmissionsMode } from '../../shared/enums/pcf';
import { PCFRequestStatus } from '../../shared/enums/pcf-request';
import { ToastSeverity } from '../../shared/enums/toast-severity';
import { getPaginatorTemplate } from '../../shared/helpers/get-paginator-template';
import { IComponent } from '../../shared/interfaces/IComponent';
import ComponentId from './components/ComponentId';
import ComponentName from './components/ComponentName';
import ComponentPcfValue from './components/ComponentPcfValue';
import ComponentsHeader from './components/ComponentsHeader';
import styles from './ComponentsPage.module.css';

const COMPLETED_STATUSES = [
  PCFRequestStatus.Complete,
  PCFRequestStatus.Updated,
  PCFRequestStatus.Outdated,
];

const ComponentsPage = () => {
  // Navigate to products page for BASF User
  const { isBasfUser } = useAuth();

  if (isBasfUser) {
    return <Navigate to={AppRoutes.Products} replace />;
  }

  const { skip, size, sort, filters, setParams } = useComponentsTableQueryParams();
  const [sortField, sortOrder] = sort ? sort.split('-') : [undefined, undefined];

  // Search params (filters) from the URL
  const [biogenicEmissionsMode, setBiogenicEmissionsMode] = useState<PCFBiogenicEmissionsMode>(
    PCFBiogenicEmissionsMode.Exclude,
  );

  const [totalRecords, setTotalRecords] = useState<number>(0);

  // For requesting pcf for components with region, to disable the Request Pcf button for the requested component
  // It is a map of product_id (mysql id) to boolean, where boolean is true if the component is requested
  // So that you can request pcf for multiple components at the same time (USING BUTTONS IN EACH ROW)
  const [selectedComponentsToRequestPcf, setSelectedComponentsToRequestPcf] = useState<
    Record<number, boolean>
  >({});

  // For bulk pcf requesting, to apply Optimistic UI strategy, to show those components as "Pending" in the table
  // Without them actually being "Pending" in the database
  // Those components will be declared in the BulkRequestPcfsDialog
  const [componentsToShowPendingDuringBulk, setComponentsToShowPendingDuringBulk] = useState<
    IComponent[]
  >([]);

  const { notify } = useNotificationContext();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { openDialog } = useDialogContext();

  /* eslint-disable camelcase */
  const { mutate: createPcfRequest } = useCreatePcfRequest({
    onMutate: (variables) => {
      // Make the RequestPcf button disabled for the requested component
      setSelectedComponentsToRequestPcf((prev) => {
        return { ...prev, [variables.componentId as number]: true };
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['components'] });
      queryClient.invalidateQueries({ queryKey: ['requests/component-tasks'] });
    },
    onError: () => {
      notify({
        severity: ToastSeverity.ERROR,
        summary: t('DetailsContent.pcfRequestFailed'),
        detail: t('DetailsContent.pleaseTryAgainLater'),
        life: 5000,
      });
    },
    onSettled: (data, error, variables) => {
      setSelectedComponentsToRequestPcf((prev) => {
        // After half a second, set the component to false to enable the button again
        // Make the RequestPcf button enabled for the requested component at the end
        return { ...prev, [variables.componentId as number]: false };
      });
    },
  });
  /* eslint-enable camelcase */

  // RQ Query to get components
  const { data, isLoading, isError, isSuccess } = useGetPaginatedComponents({
    ...filters,
    sort,
    skip,
    size,
  });

  const fetchedComponents = data?.data ?? [];

  // State to store the fetched components (initially start with fetchedComponents, use cache if needed)
  const [components, setComponents] = useState<IComponent[]>(fetchedComponents);

  useEffect(() => {
    if (fetchedComponents && isSuccess) {
      setComponents(fetchedComponents);
      setTotalRecords(data?.pagination?.total || 0);
    }
  }, [fetchedComponents, isSuccess]);

  // Listen to componentsToShowPendingDuringBulk changes
  // So that during their bulk-request process, we can show those selected components as "Pending" in the table
  useEffect(() => {
    if (componentsToShowPendingDuringBulk.length) {
      setComponents((prev) => {
        return prev.map((component) => {
          const optimisticallyUpdatedComponent = componentsToShowPendingDuringBulk.find((c) => {
            return c.productId === component.productId;
          });

          return optimisticallyUpdatedComponent ?? component;
        });
      });
    }
  }, [componentsToShowPendingDuringBulk]);

  const onRequestPCFButtonClick = useCallback((component: IComponent) => {
    // If component has no region, show the Dialog
    if (!component.region) {
      openDialog('request-pcf-dialog', { component });
    } else {
      // If component already has a region, fetch PCF data without showing any Dialog and refresh components

      createPcfRequest({
        componentId: component.productId,
      });
    }
  }, []);

  if (isError) {
    return <p className='text-red-500'>{t('componentsPage.failedToGetComponents')}</p>;
  }

  const componentNameHeaderTemplate = (): JSX.Element => {
    return (
      <div className='flex flex-column gap-1'>
        <span>{t('componentsPage.componentName')}</span>
        <span className='text-sm font-light font-italic text-gray-700'>
          {t('componentsPage.yourName')}
        </span>
      </div>
    );
  };

  const componentNameTemplate = (component: IComponent) => {
    const isClickable = COMPLETED_STATUSES.includes(component.status);

    return (
      <ComponentName
        componentId={component.productId}
        componentName={component.productName}
        ownProductName={component.ownProductName}
        sustainableAlternativeAvailable={component.sustainableAlternativeAvailable}
        href={isClickable ? `/components/${component.productId}/component-details` : undefined}
      />
    );
  };

  const componentIdHeaderTemplate = (): JSX.Element => {
    return (
      <div className='flex flex-column gap-1'>
        <span>{t('componentsPage.componentId')}</span>
        <span className='text-sm font-light font-italic text-gray-700'>
          {t('componentsPage.yourId')}
        </span>
      </div>
    );
  };

  const componentIdTemplate = (component: IComponent) => {
    const isClickable = COMPLETED_STATUSES.includes(component.status);

    return (
      <ComponentId
        componentId={component.productCid}
        ownProductId={component.ownProductId}
        href={isClickable ? `/components/${component.productId}/component-details` : undefined}
      />
    );
  };

  const regionTemplate = (component: IComponent) => {
    return <ComponentRegion component={component} />;
  };

  const componentStatusTemplate = (component: IComponent) => {
    return (
      <RequestStatus status={component.status} isFootprintUpdated={component.isFootprintUpdated} />
    );
  };

  const pcfsTemplate = (component: IComponent) => {
    if (!component.requestId || !COMPLETED_STATUSES.includes(component.status)) {
      return;
    }

    return (
      <div className='flex justify-content-start'>
        {/* Download PDF Button */}
        {component.isPdfDownloadAvailable && (
          <DownloadPdfFileButton
            requestId={component.requestId}
            isDisabled={component.status === PCFRequestStatus.Outdated}
          />
        )}

        {/* Download JSON Button */}
        <DownloadJsonFileButton
          componentName={component.productName}
          componentId={component.productCid}
          requestId={component.requestId}
        />
      </div>
    );
  };

  const requestPcfTemplate = (component: IComponent) => {
    // If the component is already requested, don't show the button
    if (
      component.requestId != null &&
      component.productId != null &&
      !selectedComponentsToRequestPcf[component.productId]
    )
      return;

    // If component is complete or pending, don't show the button
    if (
      component.status === PCFRequestStatus.Complete ||
      component.status === PCFRequestStatus.Pending
    )
      return;

    return (
      <Button
        className='w-fit px-1 xl:px-3 text-sm xl:text-base'
        label={t('componentsPage.requestPcf')}
        disabled={selectedComponentsToRequestPcf[component.productId]}
        loading={selectedComponentsToRequestPcf[component.productId]}
        onClick={() => {
          return onRequestPCFButtonClick(component);
        }}
      />
    );
  };

  const pcfHeaderTemplate = (): JSX.Element => {
    return (
      <div className='flex flex-column gap-1'>
        <span>{t('componentsPage.pcfValue')}</span>
        <span className='text-sm font-light'>{t('componentsPage.excludingBiogenic')}</span>
      </div>
    );
  };

  const pcfValueTemplate = (component: IComponent) => {
    return (
      <ComponentPcfValue component={component} biogenicEmissionsMode={biogenicEmissionsMode} />
    );
  };

  const supplierNameTemplate = (component: IComponent) => {
    return (
      <span>
        {component?.supplierCompanyName ?? component?.otherRequestData?.recipient_company_name}
      </span>
    );
  };

  const isTableEmpty =
    !isLoading &&
    data?.data.length === 0 &&
    Object.values(filters).filter((value) => {
      return Boolean(value);
    }).length === 0;

  return (
    <div className={classNames('px-3 py-4  sm:p-4 flex flex-column', styles['components-page'])}>
      <h1 className='text-2xl md:text-4xl'>
        <Trans i18nKey='componentsPage.heading' />
      </h1>

      {
        // If no components, show the empty screen
        isTableEmpty ? (
          <EmptyScreen imageSrc={EmptyComponentsImg}>
            <h4>{t('emptyComponentsPage.heading')}</h4>
            <p>{t('emptyComponentsPage.description')}</p>
            <Button
              className='mx-auto mb-4 px-3'
              label={t('emptyComponentsPage.requestPcfForComponent')}
              onClick={() => {
                openDialog('request-additional-component-dialog');
              }}
            />
          </EmptyScreen>
        ) : (
          <div className='flex flex-column gap-4'>
            <div className={styles['components-table']}>
              <DataTable
                loading={isLoading}
                value={components}
                scrollable
                paginator
                paginatorTemplate={getPaginatorTemplate({
                  totalRecords,
                  rowsPerPage: size,
                  skip,
                  onNext: () => {
                    setParams({ page: (skip + size) / size + 1 });
                  },
                  onPrev: () => {
                    setParams({ page: (skip + size) / size - 1 });
                  },
                })}
                rows={size}
                lazy
                paginatorPosition='top'
                header={
                  <ComponentsHeader
                    biogenicEmissionsMode={biogenicEmissionsMode}
                    setBiogenicEmissionsMode={setBiogenicEmissionsMode}
                    componentsToShowPendingDuringBulk={componentsToShowPendingDuringBulk}
                    setComponentsToShowPendingDuringBulk={setComponentsToShowPendingDuringBulk}
                  />
                }
                sortField={sortField}
                sortOrder={sortField ? (sortOrder === 'desc' ? -1 : 1) : undefined}
                onSort={(event: DataTableStateEvent) => {
                  const newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
                  setParams({
                    sort: `${event.sortField}-${newSortOrder}`,
                    page: 1,
                  });
                }}
                pt={{
                  wrapper: {
                    className: styles['table-wrapper'],
                  },
                }}
                tableStyle={{ tableLayout: 'fixed', minWidth: '1200px' }}
                emptyMessage={t('global.emptyTableMessage')}
              >
                <Column
                  header={componentNameHeaderTemplate}
                  body={componentNameTemplate}
                  sortable
                  style={{ width: '20%', wordBreak: 'break-word' }}
                  frozen
                  sortField='componentName'
                />
                <Column
                  header={componentIdHeaderTemplate}
                  body={componentIdTemplate}
                  sortable
                  sortField='componentId'
                  style={{ width: '15%', wordBreak: 'break-word' }}
                />
                <Column
                  header={t('componentsPage.supplierName')}
                  sortable
                  sortField='supplierCompanyName'
                  body={supplierNameTemplate}
                  style={{ width: '15%', wordBreak: 'break-word' }}
                />
                <Column
                  header={t('componentsPage.location')}
                  body={regionTemplate}
                  sortable
                  sortField='location'
                  style={{ width: '10%', wordBreak: 'break-word' }}
                />
                <Column
                  header={pcfHeaderTemplate}
                  body={pcfValueTemplate}
                  sortable
                  sortField='pcfValue'
                  style={{ width: '10%' }}
                />
                <Column
                  header={
                    <div className='flex gap-2'>
                      {t('componentsPage.pcfs')}
                      <Button
                        className='p-0 flex justify-content-start info-icon cursor-pointer'
                        link
                        icon={InfoIconPrimary}
                        onClick={() => {
                          openDialog('pcf-info-dialog');
                        }}
                      />
                    </div>
                  }
                  body={pcfsTemplate}
                  style={{ width: '10%' }}
                />

                <Column
                  header={t('componentsPage.status')}
                  body={componentStatusTemplate}
                  style={{ width: '140px' }}
                />
                <Column body={requestPcfTemplate} bodyClassName='text-center' />
              </DataTable>
            </div>
          </div>
        )
      }
    </div>
  );
};

export default ComponentsPage;
